import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColDef, ValueFormatterParams } from 'ag-grid-community';

import { Volume, VolumeResponse } from '~/shared/types/api';
import { useXFetch } from '~/shared/utils/useXFetch';
import Table from '~/shared/components/Table';
import Typography from '~/shared/components/Typography';
import { DEFAULT_REFETCH_INTERVAL_MS } from '~/shared/constants/polling';
import SizeCell from '~/shared/components/SizeCell';
import { formatVolumeMetric } from '~/shared/utils/formatNumber';
import { appendAccountsScope, getVolumeDataQueryScopeParts } from '~/shared/utils/organizationUtils';
import { useAuthContext } from '~/shared/context/useAuthContext';

import { baseUrl } from '../FleetManagerPage/helpers';
import { getFilterSearchParams } from '../common/helpers';

export default function DatafyVolumes({
  volume,
  refreshKey,
  accountId,
}: {
  volume: Volume;
  refreshKey?: string;
  accountId?: string;
}) {
  const { volumeId } = volume;
  const { accounts, activeTenantId, loading: authLoading, isAuthenticated } = useAuthContext();

  const selectedAccountIds = useMemo(
    () => (accountId ? [accountId] : undefined),
    [accountId]
  );

  const scopeParts = useMemo(
    () => getVolumeDataQueryScopeParts(accounts, activeTenantId, selectedAccountIds),
    [accounts, activeTenantId, selectedAccountIds]
  );

  const api = useXFetch();
  const { data } = useQuery({
    queryKey: ['getDatafiedVolumes', volumeId, refreshKey, ...scopeParts],
    queryFn: async () => {
      const qs = new URLSearchParams(
        getFilterSearchParams([
          { field: 'monitoring', value: 'datafied', op: 'equals' },
          { field: 'sourceVolume', op: 'equals', value: volumeId! },
        ])
      );
      appendAccountsScope(qs, accounts, activeTenantId, selectedAccountIds);
      return api<VolumeResponse>(`${baseUrl}?${qs.toString()}`);
    },
    enabled: isAuthenticated && !authLoading,
    refetchOnWindowFocus: false,
    refetchInterval: DEFAULT_REFETCH_INTERVAL_MS,
  });

  return (
    <div className="flex flex-col min- h-[423px] 2xl:h-[428px] overflow-hidden">
      <div className="flex gap-2 px-2 pb-6 mb-3 border-b   border-gray-300">
        <Typography variant="H5" className=" bg-gree">
          Datafy Volumes
        </Typography>

        <Counter count={data?.volumes.length ?? 0} />
      </div>

      <div
        className={`flex flex-col h-[480px] [&_.ag-root-wrapper]:!border-0 [&_.ag-root-wrapper]:!rounded-none ${
          (data?.volumes.length ?? 0) > 5 ? '[&_.ag-row-last]:!border-b-0' : ''
        }`}
      >
        <Table
          columnDefs={columnDefs}
          rowData={data?.volumes || []}
          headerHeight={48}
          itemsName="volumes"
        />
      </div>
    </div>
  );
}

const columnDefs: ColDef<Volume>[] = [
  {
    headerName: 'Datafy Volume ID',
    field: 'volumeId',
    flex: 1,
    minWidth: 200,
    width: 200,
  },
  {
    headerName: 'Size',
    field: 'diskSize',
    flex: 1,
    minWidth: 120,
    width: 120,
    cellRenderer: ({ value }: ValueFormatterParams<Volume>) => <SizeCell size={value} />,
  },
  {
    headerName: 'IOPS',
    field: 'iops',
    flex: 1,
    minWidth: 96,
    width: 96,
    valueFormatter: ({ data }) => formatVolumeMetric(data?.iops),
  },
  {
    headerName: 'Throughput',
    field: 'throughput',
    flex: 1,
    minWidth: 112,
    width: 112,
    valueFormatter: ({ data }) => formatVolumeMetric(data?.throughput),
  },
];

export const Counter = ({ count }: { count: number }) => {
  return (
    <p className="grid place-items-center px-1.5 py-0.5 min-w-6 bg-bg rounded text-gray-700 text-xs font-[700]">
      {count}
    </p>
  );
};
